import { useCallback, useEffect, useState } from 'react';
import { useScopedUrl } from '../nav/scopeOrg';
import { API_BASE } from '../client/config';

export type DiagnosticStatus = 'online' | 'fault' | 'offline';
type DiagnosticHistoryState = {
  health: 'healthy' | 'playback_error' | 'playback_stalled'; errorCode: string | null;
  recoveryCount: number; recoveryResult: 'pending' | 'success' | 'failed' | null;
};
export type DiagnosticHistory = { id: number; createdAt: string; before: DiagnosticHistoryState | null; after: DiagnosticHistoryState };
export type DeviceDiagnostic = {
  orgId: string; deviceId: string; status: DiagnosticStatus; reason: string;
  lastSeenAt: string | null; reportedAt: string | null; lastProgressAt: string | null;
  lastRecoveryAt: string | null; lastRecoveryResult: 'pending' | 'success' | 'failed' | null;
  report: { sourceKind: string; playbackState: string; progressFrame: number | null; progressTime: number | null;
    responseDelayMs: number | null; errorCode: string | null; recoveryCount: number } | null;
  history?: DiagnosticHistory[] | null;
  connections?: Array<{ id: number; createdAt: string; event: 'connected' | 'disconnected'; code: number | null; durationMs: number | null }> | null;
};
const POLL_MS = 15_000;
const BATCH_SIZE = 100;
const REASONS: Record<string, string> = {
  healthy: '정상 보고', no_diagnostics: '진단 보고 대기', heartbeat_timeout: '90초 이상 연결 확인 없음',
  playback_error: '재생 오류', playback_stalled: '재생 진행 정체', invalid_diagnostics: '진단 데이터 오류',
  progress_unavailable: '재생 진행 정보 없음',
};

/** One polling loop for the entire list; no per-row timers or overlapping requests. */
export function useDeviceDiagnostics(deviceIds: readonly string[], enabled = true) {
  const scoped = useScopedUrl();
  const idsKey = [...new Set(deviceIds)].sort().join(',');
  const [data, setData] = useState<Record<string, DeviceDiagnostic>>({});
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(value => value + 1), []);

  useEffect(() => {
    let disposed = false;
    let accessBlocked = false;
    let timer: number | null = null;
    let controller: AbortController | null = null;
    const ids = idsKey ? idsKey.split(',') : [];
    const stop = () => {
      controller?.abort();
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
    };
    const load = async () => {
      stop();
      if (disposed || accessBlocked || !enabled || !ids.length || document.hidden) return;
      const request = new AbortController(); controller = request;
      let forbidden = false;
      setLoading(true);
      try {
        const next: Record<string, DeviceDiagnostic> = {};
        for (let offset = 0; offset < ids.length; offset += BATCH_SIZE) {
          const batch = ids.slice(offset, offset + BATCH_SIZE);
          const single = ids.length === 1;
          const url = single ? `${API_BASE}/api/devices/${encodeURIComponent(batch[0]!)}/diagnostics`
            : `${API_BASE}/api/devices/diagnostics?${new URLSearchParams({ ids: batch.join(',') })}`;
          const response = await fetch(scoped(url), { credentials: 'include', cache: 'no-store', signal: request.signal });
          if (request.signal.aborted || disposed) return;
          if (!response.ok) {
            if ([401, 403, 404].includes(response.status)) { forbidden = true; accessBlocked = true; setBlocked(true); }
            throw new Error([401, 403, 404].includes(response.status) ? '기기 접근 권한이 변경되어 진단을 숨겼습니다.' : '진단을 불러오지 못했습니다.');
          }
          const body = await response.json() as DeviceDiagnostic | { items: DeviceDiagnostic[] };
          if (request.signal.aborted || disposed) return;
          const items = single ? [body as DeviceDiagnostic] : (body as { items: DeviceDiagnostic[] }).items;
          // The server answers for the devices it still has. A device deleted or
          // revoked between this page's list and this request is left out, and
          // demanding an exact count turned that ordinary race into a blocking
          // error. Responses for devices that were never asked about, or the
          // same device twice, are still rejected.
          if (!Array.isArray(items) || items.some(item =>
            !item || !batch.includes(item.deviceId) || !['online', 'fault', 'offline'].includes(item.status))
            || new Set(items.map(item => item.deviceId)).size !== items.length) throw new Error('진단 응답이 요청한 기기와 일치하지 않습니다.');
          for (const item of items) next[item.deviceId] = item;
        }
        setData(next); setError(null); setBlocked(false);
      } catch (reason) {
        if (!disposed && !request.signal.aborted) {
          setData({}); setError(reason instanceof Error ? reason.message : '진단 조회 실패');
        }
      } finally {
        if (!disposed && !request.signal.aborted) {
          setLoading(false);
          if (!forbidden) timer = window.setTimeout(() => void load(), POLL_MS);
        }
      }
    };
    const reset = () => { stop(); setData({}); setError(null); setBlocked(false); setLoading(false); };
    const reload = () => { accessBlocked = false; reset(); void load(); };
    const visibility = () => { if (document.hidden) { stop(); setLoading(false); } else if (!accessBlocked) reload(); };
    const logout = () => { accessBlocked = true; reset(); setBlocked(true); };
    reload();
    window.addEventListener('bricklit:scope-changed', reload);
    window.addEventListener('bricklit:auth-changed', reload);
    window.addEventListener('bricklit:logout', logout);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      disposed = true; stop();
      window.removeEventListener('bricklit:scope-changed', reload);
      window.removeEventListener('bricklit:auth-changed', reload);
      window.removeEventListener('bricklit:logout', logout);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [idsKey, enabled, revision]);
  return { data: enabled ? data : {}, error, blocked, loading, refresh };
}

export function DeviceDiagnosticsBadge({ diagnostic }: { diagnostic?: DeviceDiagnostic | undefined }) {
  if (!diagnostic) return <span className="text-text-dim">진단 대기</span>;
  const labels: Record<DiagnosticStatus, string> = { online: '온라인', fault: '장애', offline: '오프라인' };
  const colors: Record<DiagnosticStatus, string> = { online: 'bg-green', fault: 'bg-red', offline: 'bg-text-faint' };
  return <span title={REASONS[diagnostic.reason] ?? diagnostic.reason} className="inline-flex items-center gap-1.5 whitespace-nowrap">
    <span className={`inline-block h-2 w-2 rounded-full ${colors[diagnostic.status]}`} />{labels[diagnostic.status]}
  </span>;
}

const displayTime = (value: string | null) => value ? new Date(value).toLocaleString('ko-KR') : '-';
/**
 * 기기 진단 카드. 접속 이력·장애복구 이력은 기본 접힘(2026-09-14, 대교) — 매일 보는 건 상태·사유고
 * 이력은 문제가 있을 때 펼쳐 본다. className으로 바깥 여백을 호출자가 정한다(페이지 mx-6 mt-4, 모달 0).
 */
export function DeviceDiagnostics({ deviceId, onScopeBlocked, className = 'mx-6 mt-4' }: { deviceId: string; onScopeBlocked?: () => void; className?: string }) {
  const diagnostics = useDeviceDiagnostics([deviceId]);
  useEffect(() => { if (diagnostics.blocked) onScopeBlocked?.(); }, [diagnostics.blocked, onScopeBlocked]);
  const item = diagnostics.data[deviceId];
  const recovery = { pending: '진행 중', success: '성공', failed: '실패' };
  return <section className={`${className} rounded-md border border-line bg-panel p-5`} aria-label="기기 진단">
    <div className="flex items-center justify-between gap-3">
      <h2 className="text-[15px] font-semibold">기기 진단</h2>
      <DeviceDiagnosticsBadge {...(item ? { diagnostic: item } : {})} />
    </div>
    {diagnostics.error && <p role="alert" className="mt-3 text-[13px] text-red">{diagnostics.error}</p>}
    {!item && !diagnostics.error && <p role="status" className="mt-3 text-[13px] text-text-dim">진단 보고를 확인하는 중…</p>}
    {item && <>
      <p className="mt-2 text-[13px] text-text-dim">{REASONS[item.reason] ?? item.reason}</p>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-[13px]">
        {[
          ['마지막 연결 확인', displayTime(item.lastSeenAt)], ['진단 수신', displayTime(item.reportedAt)],
          ['소스 / 재생', item.report ? `${item.report.sourceKind} / ${item.report.playbackState}` : '-'],
          ['마지막 진행 확인', displayTime(item.lastProgressAt)],
          ['재생 프레임 / 시간', item.report ? `${item.report.progressFrame ?? '-'} / ${item.report.progressTime ?? '-'}초` : '-'],
          ['응답 지연', item.report?.responseDelayMs == null ? '-' : `${item.report.responseDelayMs}ms`],
          ['현재 오류 코드', item.report?.errorCode ?? '-'], ['보고된 복구 횟수', String(item.report?.recoveryCount ?? 0)],
          ['마지막 복구 확인', displayTime(item.lastRecoveryAt)], ['복구 결과', item.lastRecoveryResult ? recovery[item.lastRecoveryResult] : '-'],
        ].map(([label, value]) => <div key={label}><dt className="text-text-dim">{label}</dt><dd className="mt-1 break-words">{value}</dd></div>)}
      </dl>
      {item.connections != null && <details className="mt-5 border-t border-line pt-4">
        <summary className="cursor-pointer select-none text-[14px] font-semibold">기기 접속 이력 <span className="ml-1 text-[12px] font-normal text-text-dim">{item.connections.length}건</span></summary>
        <p className="mt-1 text-[12px] text-text-dim">현재 그룹의 WebSocket 접속·종료 최근 50건</p>
        {item.connections.length === 0 ? <p className="mt-2 text-[13px] text-text-dim">기록된 접속 이력이 없습니다. 다음 접속부터 기록됩니다.</p>
          : <ol aria-label="기기 접속 이력" className="mt-2 divide-y divide-line text-[13px]">{item.connections.map(entry => <li key={entry.id} className="py-2">
            {new Date(entry.createdAt).toLocaleString()} · {entry.event === 'connected' ? '접속' : '연결 종료'}
            {entry.durationMs != null && ` · ${Math.round(entry.durationMs / 1000)}초 연결`}
            {entry.code != null && ` · 종료 코드 ${entry.code}`}
          </li>)}</ol>}
      </details>}
      {item.history != null && <details className="mt-5 border-t border-line pt-4">
        <summary className="cursor-pointer select-none text-[14px] font-semibold">장애·복구 이력 <span className="ml-1 text-[12px] font-normal text-text-dim">{item.history.length}건</span></summary>
        <p className="mt-1 text-[12px] text-text-dim">현재 그룹에서 보고된 최근 전이 최대 20건</p>
        {item.history.length === 0 ? <p className="mt-2 text-[13px] text-text-dim">기록된 장애·복구 전이가 없습니다.</p>
          : <ol aria-label="장애·복구 이력" className="mt-2 divide-y divide-line text-[13px]">
            {item.history.map(entry => <li key={entry.id} className="py-2">
              <time dateTime={entry.createdAt} className="text-text-dim">{displayTime(entry.createdAt)}</time>
              <p>{entry.before ? REASONS[entry.before.health] : '첫 보고'} → {REASONS[entry.after.health]}</p>
              <p>복구 횟수 {entry.before?.recoveryCount ?? 0} → {entry.after.recoveryCount} · 결과 {entry.before?.recoveryResult ? recovery[entry.before.recoveryResult] : '-'} → {entry.after.recoveryResult ? recovery[entry.after.recoveryResult] : '-'}</p>
              {entry.after.errorCode && <p>오류 코드 {entry.after.errorCode}</p>}
            </li>)}
          </ol>}
      </details>}
    </>}
    <button type="button" onClick={diagnostics.refresh} disabled={diagnostics.loading}
      className="mt-4 rounded border border-line px-3 py-1.5 text-[13px] disabled:opacity-50">다시 확인</button>
  </section>;
}
